// src/components/tutor/availability/TutorAvailabilityModal.js
// Redesigned to match the EmployeeAvailability modal aesthetic with enter animation.

import React, { useMemo, useState } from "react";
import { buildTimeOptions, fmtTimeLabel } from "./tutorAvailabilityUtils";

// ── Enter animation injected once ──────────────────────────────────────────────
const STYLE_ID = "tutor-av-modal-styles";
if (typeof document !== "undefined" && !document.getElementById(STYLE_ID)) {
  const s = document.createElement("style");
  s.id = STYLE_ID;
  s.textContent = `
    @keyframes tav-overlay-in { from { opacity: 0; } to { opacity: 1; } }
    @keyframes tav-card-in    { from { opacity: 0; transform: scale(0.96) translateY(6px); } to { opacity: 1; transform: scale(1) translateY(0); } }
    .tav-overlay { animation: tav-overlay-in 0.15s ease forwards; }
    .tav-card    { animation: tav-card-in    0.2s cubic-bezier(0.16,1,0.3,1) forwards; }
  `;
  document.head.appendChild(s);
}

export default function TutorAvailabilityModal({
  open,
  onClose,
  date,
  initialStartMin = 9 * 60,
  initialEndMin   = 17 * 60,
  onSave,
  onDelete,
  mode     = "create", // "create" | "edit"
  isLocked = false,
}) {
  const options = useMemo(() => buildTimeOptions(), []);
  const [startMin, setStartMin] = useState(initialStartMin);
  const [endMin,   setEndMin]   = useState(initialEndMin);

  const dateTimestamp = date?.getTime();
  React.useEffect(() => {
    if (!open) return;
    setStartMin(initialStartMin);
    setEndMin(initialEndMin);
  }, [open, initialStartMin, initialEndMin, dateTimestamp]);

  if (!open || !date) return null;

  const canSave = endMin > startMin && !isLocked;

  return (
    <div
      className="tav-overlay"
      style={{
        position: "fixed", inset: 0,
        background: "rgba(15,23,42,0.35)",
        display: "flex", alignItems: "center", justifyContent: "center",
        zIndex: 999, padding: 20,
      }}
      onMouseDown={onClose}
    >
      <div
        className="tav-card"
        style={{
          width: "min(480px, 100%)",
          background: "#fff",
          borderRadius: 14,
          border: "1px solid #e5e7eb",
          boxShadow: "0 20px 60px rgba(15,23,42,0.18)",
          overflow: "hidden",
        }}
        onMouseDown={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{ padding: "16px 20px 14px", borderBottom: "1px solid #f3f4f6" }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: "#111827", letterSpacing: "-0.2px" }}>
            {mode === "edit" ? "Edit Availability" : "Add Availability"}
          </div>
          <div style={{ fontSize: 12, color: "#9ca3af", marginTop: 3 }}>
            {date.toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric", year: "numeric" })}
          </div>
        </div>

        {/* Body */}
        <div style={{ padding: "16px 20px", display: "grid", gap: 14 }}>
          {/* Lock warning */}
          {mode === "edit" && isLocked && (
            <div style={{
              padding: "10px 12px",
              background: "#fff7ed",
              border: "1px solid #fed7aa",
              borderRadius: 8,
              color: "#9a3412",
              fontSize: 12,
              fontWeight: 500,
              lineHeight: 1.5,
            }}>
              This block is within 7 days and cannot be edited. Please contact Admin.
            </div>
          )}

          {/* Start time */}
          <div>
            <div style={{ fontSize: 11, fontWeight: 600, color: "#374151", marginBottom: 6, textTransform: "uppercase", letterSpacing: "0.4px" }}>
              Start Time
            </div>
            <select
              value={startMin}
              onChange={(e) => setStartMin(parseInt(e.target.value, 10))}
              disabled={isLocked}
              style={{
                width: "100%", padding: "9px 12px",
                borderRadius: 8, border: "1px solid #e5e7eb",
                fontSize: 13, color: "#111827", background: "#fff",
                outline: "none", cursor: isLocked ? "not-allowed" : "pointer",
                opacity: isLocked ? 0.5 : 1,
              }}
            >
              {options.slice(0, -1).map((minute) => (
                <option key={minute} value={minute}>{fmtTimeLabel(minute)}</option>
              ))}
            </select>
          </div>

          {/* End time */}
          <div>
            <div style={{ fontSize: 11, fontWeight: 600, color: "#374151", marginBottom: 6, textTransform: "uppercase", letterSpacing: "0.4px" }}>
              End Time
            </div>
            <select
              value={endMin}
              onChange={(e) => setEndMin(parseInt(e.target.value, 10))}
              disabled={isLocked}
              style={{
                width: "100%", padding: "9px 12px",
                borderRadius: 8, border: "1px solid #e5e7eb",
                fontSize: 13, color: "#111827", background: "#fff",
                outline: "none", cursor: isLocked ? "not-allowed" : "pointer",
                opacity: isLocked ? 0.5 : 1,
              }}
            >
              {options.slice(1).map((minute) => (
                <option key={minute} value={minute}>{fmtTimeLabel(minute)}</option>
              ))}
            </select>
            {!isLocked && endMin <= startMin && (
              <div style={{ marginTop: 6, fontSize: 12, color: "#b91c1c", fontWeight: 500 }}>
                End time must be after start time.
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div style={{
          padding: "14px 20px",
          borderTop: "1px solid #f3f4f6",
          display: "flex",
          justifyContent: "space-between",
          gap: 10,
        }}>
          <button
            onClick={onClose}
            style={{
              padding: "8px 14px", borderRadius: 8,
              border: "1px solid #e5e7eb", background: "#fff",
              fontSize: 13, fontWeight: 500, color: "#374151", cursor: "pointer",
            }}
          >
            Cancel
          </button>

          <div style={{ display: "flex", gap: 8 }}>
            {mode === "edit" && (
              <button
                onClick={onDelete}
                disabled={isLocked}
                style={{
                  padding: "8px 14px", borderRadius: 8,
                  border: "1px solid #fecaca", background: "#fff",
                  color: isLocked ? "#fca5a5" : "#b91c1c",
                  fontSize: 13, fontWeight: 500,
                  cursor: isLocked ? "not-allowed" : "pointer",
                  opacity: isLocked ? 0.5 : 1,
                }}
              >
                Delete
              </button>
            )}

            <button
              onClick={() => onSave({ startMin, endMin })}
              disabled={!canSave}
              style={{
                padding: "8px 16px", borderRadius: 8,
                border: "1px solid #1d4ed8",
                background: canSave ? "#2563eb" : "#93c5fd",
                color: "#fff", fontSize: 13, fontWeight: 500,
                cursor: canSave ? "pointer" : "not-allowed",
              }}
            >
              Save
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
